<template>
  <div class="w-100">
    <basic-subheader title="Chất lượng AI" />
    <b-overlay :show="loading">
      <div class="card card-custom gutter-b">
        <div class="card-body">
          <p class="text-muted mb-6">
            Số liệu đọc thẳng từ hệ thống (không cần cập nhật tay): nháp AI cho coach Zalo, tốc độ, chi phí và bài học AI rút từ câu coach sửa.
            Ngày theo giờ Việt Nam. Bài học mới được rút mỗi đêm lúc 01:00.
          </p>

          <b-row class="mb-6">
            <b-col md="3">
              <label for="q-from">Từ ngày</label>
              <b-form-input id="q-from" v-model="filter.from" type="date" />
            </b-col>
            <b-col md="3">
              <label for="q-to">Đến ngày</label>
              <b-form-input id="q-to" v-model="filter.to" type="date" />
            </b-col>
            <b-col md="3">
              <label for="q-type">Loại tin</label>
              <b-form-select id="q-type" v-model="filter.kpiType" :options="typeOptions" />
            </b-col>
            <b-col md="3" class="d-flex align-items-end">
              <b-button variant="primary" class="mr-2" @click="loadData">Xem</b-button>
              <b-button v-for="n in [7, 30, 90]" :key="n" size="sm" variant="light" class="mr-1" @click="quickRange(n)">{{ n }} ngày</b-button>
            </b-col>
          </b-row>

          <div v-if="report && report.todos && report.todos.length" class="mb-6">
            <h5>Việc cần làm <span class="text-muted font-size-sm">({{ report.todos.length }})</span></h5>
            <div v-for="(t, i) in report.todos" :key="i" class="border rounded p-3 mb-2 d-flex align-items-start">
              <b-badge :variant="todoVariant(t.level)" class="mr-3 mt-1">{{ todoLabel(t.level) }}</b-badge>
              <div class="flex-grow-1" style="min-width: 0">
                <div class="font-weight-bold">{{ t.title }}</div>
                <div class="text-muted font-size-sm">{{ t.detail }}</div>
              </div>
              <b-button v-if="t.kind === 'persona'" size="sm" variant="primary" class="ml-3" @click="approvePersona(t)">Duyệt</b-button>
            </div>
          </div>

          <b-row v-if="report" class="mb-6">
            <b-col v-for="t in tiles" :key="t.label" xl="2" md="4" cols="6" class="mb-3">
              <div class="border rounded p-3 h-100">
                <div class="text-muted font-size-sm">{{ t.label }}</div>
                <div class="font-size-h5 font-weight-bold text-nowrap">{{ t.value }}</div>
                <div v-if="t.note" class="text-muted font-size-sm">{{ t.note }}</div>
              </div>
            </b-col>
          </b-row>

          <template v-if="report">
            <b-row>
              <b-col lg="6" class="mb-6">
                <h5>Coach dùng câu AI thế nào</h5>
                <apexchart type="bar" height="300" :options="statusOptions" :series="statusSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Độ dài câu (ký tự)</h5>
                <apexchart type="line" height="300" :options="lineOptions('ký tự')" :series="lengthSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Câu kết bằng câu hỏi và câu hứa (%)</h5>
                <apexchart type="line" height="300" :options="lineOptions('%')" :series="styleSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Thời gian AI soạn câu coach (giây)</h5>
                <apexchart type="line" height="300" :options="lineOptions('giây')" :series="speedSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Chi phí Claude (USD)</h5>
                <apexchart type="bar" height="300" :options="lineOptions('USD')" :series="costSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Đọc từ cache (%) và lỗi</h5>
                <apexchart type="line" height="300" :options="lineOptions('')" :series="cacheSeries" />
              </b-col>
            </b-row>

            <b-row>
              <b-col lg="4" class="mb-6">
                <h5>Tỉ lệ dùng câu AI (cả khoảng)</h5>
                <apexchart type="donut" height="300" :options="donutOptions(['Gửi nguyên', 'Sửa rồi gửi', 'Bỏ qua', 'Chờ duyệt'], ['#1BC5BD', '#FFA800', '#F64E60', '#B5B5C3'])" :series="statusDonut" />
              </b-col>
              <b-col lg="8" class="mb-6">
                <h5>% gửi nguyên và % coach sửa theo ngày</h5>
                <apexchart type="line" height="300" :options="lineOptions('%')" :series="rateSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Theo loại tin</h5>
                <apexchart type="bar" height="300" :options="stackedBarOptions(typeLabels)" :series="typeSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Theo giờ trong ngày (giờ VN)</h5>
                <apexchart type="bar" height="300" :options="stackedBarOptions(hourLabels)" :series="hourSeries" />
              </b-col>
              <b-col lg="12" class="mb-6">
                <h5>Theo coach duyệt</h5>
                <apexchart type="bar" :height="Math.max(260, coachLabels.length * 34)" :options="coachBarOptions" :series="coachSeries" />
              </b-col>
              <b-col lg="4" class="mb-6">
                <h5>Chi phí theo agent</h5>
                <apexchart type="donut" height="300" :options="donutOptions(agentLabels)" :series="agentCostSeries" />
              </b-col>
              <b-col lg="4" class="mb-6">
                <h5>Chi phí theo model</h5>
                <apexchart type="donut" height="300" :options="donutOptions(modelLabels)" :series="modelCostSeries" />
              </b-col>
              <b-col lg="4" class="mb-6">
                <h5>Lượt gọi theo agent</h5>
                <apexchart type="bar" height="300" :options="horizontalOptions(agentLabels)" :series="agentCallSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Chi phí cộng dồn (USD)</h5>
                <apexchart type="area" height="300" :options="lineOptions('USD')" :series="cumulativeCostSeries" />
              </b-col>
              <b-col lg="6" class="mb-6">
                <h5>Lượt gọi Claude và lượt coach Zalo theo ngày</h5>
                <apexchart type="bar" height="300" :options="lineOptions('')" :series="volumeSeries" />
              </b-col>
            </b-row>

            <h5 class="mt-4">Theo loại tin</h5>
            <b-table small bordered responsive :items="report.byType" :fields="typeFields" class="mb-8" />

            <h5>Theo coach</h5>
            <b-table small bordered responsive :items="report.byCoach" :fields="coachFields" class="mb-8" />

            <h5>Theo agent</h5>
            <b-table small bordered responsive :items="report.byAgent" :fields="agentFields" class="mb-8" />

            <h5>
              Bài học AI rút từ câu coach sửa
              <span class="text-muted font-size-sm">({{ report.lessons.length }} bài trong khoảng chọn; AI dùng 8 bài gần nhất của coach phụ trách)</span>
            </h5>
            <b-table small bordered responsive :items="report.lessons" :fields="lessonFields">
              <template #cell(detail)="row">
                <b-button size="sm" variant="light" @click="row.toggleDetails">{{ row.detailsShowing ? 'Ẩn' : 'Xem' }} câu</b-button>
              </template>
              <template #row-details="row">
                <div class="font-size-sm">
                  <div><b>AI soạn:</b> {{ row.item.aiText }}</div>
                  <div><b>Coach gửi:</b> {{ row.item.coachText }}</div>
                </div>
              </template>
              <template #cell(action)="row">
                <b-button size="sm" variant="outline-danger" @click="hideLesson(row.item)">Ẩn bài học</b-button>
              </template>
            </b-table>
          </template>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import moment from 'moment';
import VueApexCharts from 'vue-apexcharts';

const TYPE_LABELS = {
  nutrition: 'Bữa ăn',
  normal_chat: 'Trò chuyện',
  blood_sugar: 'Đường huyết',
  weight: 'Cân nặng',
  non_target_chat: 'Người khác trong nhóm',
};

export default {
  name: 'AiQuality',
  components: { apexchart: VueApexCharts },
  data() {
    return {
      loading: false,
      report: null,
      filter: {
        from: moment().subtract(29, 'days').format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD'),
        kpiType: null,
      },
      typeFields: [
        { key: 'kpiType', label: 'Loại tin', formatter: (v) => TYPE_LABELS[v] || v },
        { key: 'drafts', label: 'Nháp' },
        { key: 'sentAsIs', label: 'Gửi nguyên' },
        { key: 'edited', label: 'Sửa' },
        { key: 'skipped', label: 'Bỏ qua' },
        { key: 'avgAiChars', label: 'Dài AI (ký tự)' },
      ],
      coachFields: [
        { key: 'coach', label: 'Coach' },
        { key: 'drafts', label: 'Nháp' },
        { key: 'sentAsIs', label: 'Gửi nguyên' },
        { key: 'edited', label: 'Sửa' },
        { key: 'skipped', label: 'Bỏ qua' },
        { key: 'lessons', label: 'Bài học' },
      ],
      agentFields: [
        { key: 'agent', label: 'Agent / chức năng' },
        { key: 'model', label: 'Model' },
        { key: 'calls', label: 'Lượt' },
        { key: 'errors', label: 'Lỗi' },
        { key: 'fallback', label: 'Quay về Azure' },
        { key: 'cachePct', label: '% đọc cache' },
        { key: 'costUsd', label: 'USD' },
      ],
      lessonFields: [
        { key: 'day', label: 'Ngày' },
        { key: 'kpiType', label: 'Loại tin', formatter: (v) => TYPE_LABELS[v] || v },
        { key: 'lesson', label: 'Bài học' },
        { key: 'detail', label: '' },
        { key: 'action', label: '' },
      ],
    };
  },
  computed: {
    days() {
      return (this.report?.daily || []).map((d) => moment(d.day).format('DD/MM'));
    },
    typeOptions() {
      const types = Object.keys(TYPE_LABELS);
      return [{ value: null, text: 'Tất cả' }].concat(types.map((t) => ({ value: t, text: TYPE_LABELS[t] })));
    },
    totals() {
      const d = this.report?.daily || [];
      const sum = (k) => d.reduce((a, r) => a + (r[k] || 0), 0);
      return {
        drafts: sum('drafts'),
        sent: sum('sentAsIs'),
        edited: sum('edited'),
        skipped: sum('skipped'),
        cost: sum('costUsd'),
        errors: sum('errors'),
        fallback: sum('fallback'),
        lessons: sum('newLessons'),
      };
    },
    tiles() {
      const t = this.totals;
      const handled = t.sent + t.edited + t.skipped;
      const pct = (n) => (handled ? `${Math.round((100 * n) / handled)}%` : '—');
      const keys = this.report?.keys || {};
      const keyText = keys.lastUsed === 'new' ? 'Khoá mới' : keys.lastUsed === 'old' ? 'Khoá cũ' : '—';
      return [
        { label: 'Nháp AI', value: t.drafts },
        { label: 'Gửi nguyên', value: pct(t.sent), note: `${t.sent} câu` },
        { label: 'Coach sửa', value: pct(t.edited), note: `${t.edited} câu` },
        { label: 'Bỏ qua', value: pct(t.skipped), note: `${t.skipped} câu` },
        { label: 'Chi phí Claude', value: `${t.cost.toFixed(2)} USD`, note: `${t.errors} lỗi, ${t.fallback} lần về Azure` },
        { label: 'Khoá Claude đang dùng', value: keyText, note: `${t.lessons} bài học mới` },
      ];
    },
    statusOptions() {
      return {
        chart: { stacked: true, toolbar: { show: false } },
        xaxis: { categories: this.days },
        colors: ['#1BC5BD', '#FFA800', '#F64E60', '#B5B5C3'],
        legend: { position: 'top' },
        dataLabels: { enabled: false },
      };
    },
    statusSeries() {
      const d = this.report?.daily || [];
      return [
        { name: 'Gửi nguyên', data: d.map((r) => r.sentAsIs) },
        { name: 'Sửa rồi gửi', data: d.map((r) => r.edited) },
        { name: 'Bỏ qua', data: d.map((r) => r.skipped) },
        { name: 'Chờ duyệt', data: d.map((r) => r.pending) },
      ];
    },
    lengthSeries() {
      const d = this.report?.daily || [];
      return [
        { name: 'Câu AI', data: d.map((r) => r.avgAiChars) },
        { name: 'Câu coach sửa', data: d.map((r) => r.avgCoachChars) },
      ];
    },
    styleSeries() {
      const d = this.report?.daily || [];
      return [
        { name: '% kết bằng câu hỏi', data: d.map((r) => r.questionEndPct) },
        { name: '% câu hứa', data: d.map((r) => r.promisePct) },
      ];
    },
    speedSeries() {
      const d = this.report?.daily || [];
      return [
        { name: 'p50', data: d.map((r) => Math.round(r.p50Ms / 100) / 10) },
        { name: 'p90', data: d.map((r) => Math.round(r.p90Ms / 100) / 10) },
      ];
    },
    costSeries() {
      const d = this.report?.daily || [];
      return [{ name: 'USD', data: d.map((r) => r.costUsd) }];
    },
    statusDonut() {
      const t = this.totals;
      const pending = (this.report?.daily || []).reduce((a, r) => a + r.pending, 0);
      return [t.sent, t.edited, t.skipped, pending];
    },
    rateSeries() {
      const d = this.report?.daily || [];
      const rate = (r, k) => {
        const handled = r.sentAsIs + r.edited + r.skipped;
        return handled ? Math.round((1000 * r[k]) / handled) / 10 : null;
      };
      return [
        { name: '% gửi nguyên', data: d.map((r) => rate(r, 'sentAsIs')) },
        { name: '% coach sửa', data: d.map((r) => rate(r, 'edited')) },
        { name: '% bỏ qua', data: d.map((r) => rate(r, 'skipped')) },
      ];
    },
    typeLabels() {
      return (this.report?.byType || []).map((r) => TYPE_LABELS[r.kpiType] || r.kpiType);
    },
    typeSeries() {
      const d = this.report?.byType || [];
      return [
        { name: 'Gửi nguyên', data: d.map((r) => r.sentAsIs) },
        { name: 'Sửa rồi gửi', data: d.map((r) => r.edited) },
        { name: 'Bỏ qua', data: d.map((r) => r.skipped) },
      ];
    },
    hourLabels() {
      return (this.report?.byHour || []).map((r) => `${r.hour}h`);
    },
    hourSeries() {
      const d = this.report?.byHour || [];
      return [
        { name: 'Gửi nguyên / chờ', data: d.map((r) => r.drafts - r.edited - r.skipped) },
        { name: 'Sửa rồi gửi', data: d.map((r) => r.edited) },
        { name: 'Bỏ qua', data: d.map((r) => r.skipped) },
      ];
    },
    coachLabels() {
      return (this.report?.byCoach || []).map((r) => r.coach);
    },
    coachBarOptions() {
      return {
        chart: { stacked: true, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true } },
        xaxis: { categories: this.coachLabels },
        colors: ['#1BC5BD', '#FFA800', '#F64E60'],
        legend: { position: 'top' },
        dataLabels: { enabled: false },
      };
    },
    coachSeries() {
      const d = this.report?.byCoach || [];
      return [
        { name: 'Gửi nguyên', data: d.map((r) => r.sentAsIs) },
        { name: 'Sửa rồi gửi', data: d.map((r) => r.edited) },
        { name: 'Bỏ qua', data: d.map((r) => r.skipped) },
      ];
    },
    agentLabels() {
      return (this.report?.byAgent || []).map((r) => r.agent);
    },
    agentCostSeries() {
      return (this.report?.byAgent || []).map((r) => r.costUsd);
    },
    agentCallSeries() {
      return [{ name: 'Lượt', data: (this.report?.byAgent || []).map((r) => r.calls) }];
    },
    modelLabels() {
      return (this.report?.byModel || []).map((r) => r.model);
    },
    modelCostSeries() {
      return (this.report?.byModel || []).map((r) => r.costUsd);
    },
    cumulativeCostSeries() {
      let acc = 0;
      return [{ name: 'USD cộng dồn', data: (this.report?.daily || []).map((r) => { acc += r.costUsd; return Math.round(acc * 100) / 100; }) }];
    },
    volumeSeries() {
      const d = this.report?.daily || [];
      return [
        { name: 'Lượt gọi Claude', data: d.map((r) => r.calls) },
        { name: 'Lượt coach Zalo', data: d.map((r) => r.turns) },
        { name: 'Nháp AI', data: d.map((r) => r.drafts) },
      ];
    },
    cacheSeries() {
      const d = this.report?.daily || [];
      return [
        { name: '% đọc cache', data: d.map((r) => r.cachePct) },
        { name: 'Lỗi', data: d.map((r) => r.errors) },
        { name: 'Quay về Azure', data: d.map((r) => r.fallback) },
      ];
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    todoVariant(level) {
      return { action: 'primary', warn: 'warning', info: 'info' }[level] || 'secondary';
    },
    todoLabel(level) {
      return { action: 'Cần làm', warn: 'Chú ý', info: 'Xem' }[level] || level;
    },
    async approvePersona(t) {
      const ok = await this.$bvModal.msgBoxConfirm(`${t.title}?`, { okTitle: 'Duyệt', cancelTitle: 'Huỷ', centered: true });
      if (!ok) return;
      try {
        await this.$api.post('AI/LlmQuality/Personas/Approve', { coachId: t.coachId, version: t.version });
        this.$toastr.s({ title: 'Thành công !', msg: 'Đã duyệt văn phong, AI dùng từ tin tiếp theo' });
        this.report.todos = this.report.todos.filter((x) => x !== t);
      } catch (error) {
        this.$toastr.e(error, 'ERROR');
      }
    },
    quickRange(n) {
      this.filter.from = moment().subtract(n - 1, 'days').format('YYYY-MM-DD');
      this.filter.to = moment().format('YYYY-MM-DD');
      this.loadData();
    },
    donutOptions(labels, colors) {
      const o = {
        labels,
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, formatter: (v) => `${Math.round(v)}%` },
      };
      if (colors) o.colors = colors;
      return o;
    },
    stackedBarOptions(categories) {
      return {
        chart: { stacked: true, toolbar: { show: false } },
        xaxis: { categories },
        colors: ['#1BC5BD', '#FFA800', '#F64E60'],
        legend: { position: 'top' },
        dataLabels: { enabled: false },
      };
    },
    horizontalOptions(categories) {
      return {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true } },
        xaxis: { categories },
        legend: { show: false },
        dataLabels: { enabled: false },
      };
    },
    lineOptions(unit) {
      return {
        chart: { toolbar: { show: false } },
        xaxis: { categories: this.days },
        yaxis: { labels: { formatter: (v) => (v == null ? '' : `${Math.round(v * 10) / 10}${unit && unit !== 'ký tự' ? ` ${unit}` : ''}`) } },
        stroke: { width: 2 },
        legend: { position: 'top' },
        dataLabels: { enabled: false },
      };
    },
    async loadData() {
      this.loading = true;
      try {
        const params = { from: this.filter.from, to: this.filter.to };
        if (this.filter.kpiType) params.kpiType = this.filter.kpiType;
        const { data } = await this.$api.get('AI/LlmQuality', { params });
        this.report = data;
      } catch (error) {
        this.$toastr.e(error, 'ERROR');
      } finally {
        this.loading = false;
      }
    },
    async hideLesson(item) {
      const ok = await this.$bvModal.msgBoxConfirm('Ẩn bài học này? AI sẽ không dùng nó nữa.', {
        okTitle: 'Ẩn',
        cancelTitle: 'Huỷ',
        centered: true,
      });
      if (!ok) return;
      try {
        await this.$api.post(`AI/LlmQuality/Lessons/${item.id}/Hide`);
        this.report.lessons = this.report.lessons.filter((l) => l.id !== item.id);
        this.$toastr.s({ title: 'Thành công !', msg: 'Đã ẩn bài học' });
      } catch (error) {
        this.$toastr.e(error, 'ERROR');
      }
    },
  },
};
</script>
